import { AdminPageHeader } from "@admin-ui/components/composed/AdminPageHeader";
import { AdminLayout } from "@admin-ui/components/frames/AdminLayout";
import type { Page } from "@inertiajs/core";
import { Head, router, useForm } from "@inertiajs/react";
import {
  adminProgramLocationInclusionPath,
  adminProgramLocationInclusionsPath,
  adminProgramPath,
  adminProgramPhotoPath,
  adminProgramPhotosPath,
  adminProgramReorderingPath,
  adminProgramSessionPath,
  adminProgramSessionsPath,
  adminProgramsMapboxSearchesPath,
  adminProgramsPath,
} from "@travily/routes";
import type AdminProgramsShow from "@travily/types/pages/AdminProgramsShow";
import { type ReactNode, useCallback } from "react";
import type { MapboxSearchResult } from "@/components/admin/MapboxSearch";
import { PhotoSection } from "@/components/admin/PhotoSection";
import { ProgramExtractionPanel } from "@/components/admin/programs/ProgramExtractionPanel";
import { useAdminLayoutAccount } from "@/hooks/admin/use-admin-layout-account";
import { useAdminPhotoManager } from "@/hooks/admin/use-admin-photo-manager";
import { useAdminTabs } from "@/hooks/admin/use-admin-tabs";
import { useProgramExtractionChannel } from "@/hooks/admin/use-program-extraction-channel";
import AdminPersistentLayout from "@/layouts/admin-persistent-layout";
import { type ProgramDetailsValues, type SessionInput, ShowView } from "./show.view";

type AdminProgramsShowPageProps = AdminProgramsShow;
type ProgramData = AdminProgramsShowPageProps["program"];

const VALID_TABS = new Set(["details", "locations", "availability"]);

function extractProgramPhotos(page: Page): ProgramData["photos"] | null {
  const props = page.props as Record<string, unknown>;
  const program = props.program as Record<string, unknown> | undefined;
  return Array.isArray(program?.photos) ? (program.photos as ProgramData["photos"]) : null;
}

function AdminProgramsShowPage({
  program,
  supported_influences,
  supported_languages,
  supported_formats,
}: AdminProgramsShowPageProps) {
  const account = useAdminLayoutAccount();

  const { initialTab, onSelectionChange } = useAdminTabs({
    basePath: adminProgramPath(program.id),
    validTabs: VALID_TABS,
    defaultTab: "details",
  });

  const form = useForm<ProgramDetailsValues>({
    name: program.name ?? "",
    tagline: program.tagline ?? "",
    description: program.description ?? "",
    drop_off_available: program.drop_off_available ?? false,
    community_program: program.community_program ?? false,
    min_age: program.min_age ?? null,
    max_age: program.max_age ?? null,
    format: program.format ?? null,
    instruction_languages: program.instruction_languages ?? [],
    influences: program.influences ?? [],
  });

  const photoManager = useAdminPhotoManager({
    initialPhotos: program.photos ?? [],
    paths: {
      indexPath: adminProgramPhotosPath(program.id),
      photoPath: (sqid: string) => adminProgramPhotoPath(program.id, sqid),
      reorderPath: adminProgramReorderingPath(program.id),
    },
    extractPhotos: extractProgramPhotos,
  });

  const handleDetailsSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      form.transform((data) => ({ program: data }));
      form.patch(adminProgramPath(program.id), { preserveScroll: "errors" });
    },
    [form, program.id],
  );

  const extraction = program.extraction ?? null;
  const fieldOrigins = extraction?.field_origins ?? {};
  const touchedFields = extraction?.touched_fields ?? [];
  const extractionRunning =
    extraction?.status === "pending" || extraction?.status === "fetching" || extraction?.status === "extracting";
  useProgramExtractionChannel(program.id, Boolean(extraction) && extractionRunning);

  const revertField = useCallback(
    (field: string) => {
      switch (field) {
        case "tagline":
          form.setData("tagline", "");
          break;
        case "description":
          form.setData("description", "");
          break;
        case "min_age":
          form.setData("min_age", null);
          break;
        case "max_age":
          form.setData("max_age", null);
          break;
        case "format":
          form.setData("format", null);
          break;
        case "drop_off_available":
          form.setData("drop_off_available", false);
          break;
        case "community_program":
          form.setData("community_program", false);
          break;
        case "instruction_languages":
          form.setData("instruction_languages", []);
          break;
        case "influences":
          form.setData("influences", []);
          break;
        default:
          break;
      }
    },
    [form],
  );

  const handleAddLocation = useCallback(
    (suggestion: MapboxSearchResult) => {
      router.post(
        adminProgramLocationInclusionsPath(program.id),
        {
          suggestion: {
            mapbox_id: suggestion.mapbox_id,
            name: suggestion.name,
            country_code: suggestion.country_code,
            full_address: suggestion.full_address,
            latitude: suggestion.latitude,
            longitude: suggestion.longitude,
          },
        },
        { preserveScroll: true },
      );
    },
    [program.id],
  );

  const handleRemoveLocation = useCallback(
    (locationId: number) => {
      router.delete(adminProgramLocationInclusionPath(program.id, locationId), {
        preserveScroll: true,
      });
    },
    [program.id],
  );

  const handleAvailabilityModeChange = useCallback(
    (sessionBased: boolean) => {
      router.patch(
        adminProgramPath(program.id),
        { program: { availability_mode: sessionBased ? "sessions" : "anytime" } },
        { preserveScroll: true },
      );
    },
    [program.id],
  );

  const handleAddSession = useCallback(
    (session: SessionInput, { onSuccess }: { onSuccess: () => void }) => {
      router.post(
        adminProgramSessionsPath(program.id),
        { session },
        {
          preserveScroll: true,
          onSuccess,
        },
      );
    },
    [program.id],
  );

  const handleRemoveSession = useCallback(
    (sessionId: number) => {
      router.delete(adminProgramSessionPath(program.id, sessionId), { preserveScroll: true });
    },
    [program.id],
  );

  return (
    <AdminLayout activeUrl={adminProgramsPath()} account={account}>
      <Head title={`${program.name ?? "Program"} - Admin`} />

      <AdminPageHeader
        breadcrumbs={[{ label: "Programs", href: adminProgramsPath() }, { label: program.name ?? "Untitled program" }]}
        title={program.name ?? "Untitled program"}
        subtitle={program.account.display_name ?? `@${program.account.handle ?? "?"}`}
      />

      <div className="mx-auto flex w-full max-w-container flex-col gap-4 px-4 lg:px-8">
        <ShowView
          initialTab={initialTab}
          onTabChange={onSelectionChange}
          locationsCount={program.program_locations?.length ?? 0}
          extractionPanel={
            extraction ? <ProgramExtractionPanel programId={program.id} extraction={extraction} /> : null
          }
          values={form.data}
          errors={form.errors}
          processing={form.processing}
          supportedInfluences={supported_influences}
          supportedLanguages={supported_languages}
          supportedFormats={supported_formats}
          photoSection={<PhotoSection manager={photoManager} />}
          onFieldChange={(field, value) =>
            form.setData((prev) => ({ ...prev, [field]: value }) as ProgramDetailsValues)
          }
          onClearError={(field) => form.clearErrors(field)}
          onDetailsSubmit={handleDetailsSubmit}
          fieldOrigins={fieldOrigins}
          touchedFields={touchedFields}
          onRevertField={revertField}
          locationSearchPath={adminProgramsMapboxSearchesPath()}
          programLocations={program.program_locations ?? []}
          onAddLocation={handleAddLocation}
          onRemoveLocation={handleRemoveLocation}
          isSessionBased={program.availability_mode === "sessions"}
          onAvailabilityModeChange={handleAvailabilityModeChange}
          sessions={program.sessions ?? []}
          onAddSession={handleAddSession}
          onRemoveSession={handleRemoveSession}
        />
      </div>
    </AdminLayout>
  );
}

AdminProgramsShowPage.layout = (page: ReactNode) => <AdminPersistentLayout>{page}</AdminPersistentLayout>;

export default AdminProgramsShowPage;
